// Client-side mirror of apps/safety/functions/src/templates.ts.
// Used for the live preview in the email template editor.

export interface EmailTemplateData {
  subject: string
  title: string
  body: string
  ctaText: string | null
}

export interface TemplateTag {
  key: string
  label: string
  sample: string
}

export interface TemplateMeta {
  id: string
  label: string
  description: string
  tags: TemplateTag[]
  ctaUrl?: string
}

import { PLATFORM_URLS } from '../constants/platform-urls'

const REACT_BASE_URL = PLATFORM_URLS.safety.base

export function interpolate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => vars[key] ?? '')
}

export function buildEmailHtml(params: {
  title: string
  body: string
  ctaText?: string | null
  ctaUrl?: string
}): string {
  const cta = params.ctaText && params.ctaUrl
    ? `<table width="100%" cellpadding="0" cellspacing="0" style="margin-top:32px;">
        <tr>
          <td align="center">
            <a href="${params.ctaUrl}"
               style="display:inline-block;background:#2563eb;color:#ffffff;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;font-size:15px;font-weight:600;text-decoration:none;padding:14px 36px;border-radius:12px;">
              ${params.ctaText}
            </a>
          </td>
        </tr>
      </table>`
    : ''

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
</head>
<body style="margin:0;padding:0;background-color:#f1f5f9;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,ui-sans-serif,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f1f5f9;padding:48px 16px;">
    <tr>
      <td align="center">
        <table width="100%" cellpadding="0" cellspacing="0" style="max-width:520px;">
          <tr>
            <td align="center" style="padding-bottom:28px;">
              <p style="margin:0;font-size:20px;font-weight:700;color:#1d4ed8;">
                David's Beacon
              </p>
            </td>
          </tr>
          <tr>
            <td style="background:#ffffff;border-radius:20px;padding:40px 36px;box-shadow:0 1px 4px rgba(0,0,0,0.06);">
              <h1 style="margin:0 0 14px;font-size:20px;font-weight:700;color:#0f172a;line-height:1.35;">
                ${params.title}
              </h1>
              <p style="margin:0;font-size:15px;color:#475569;line-height:1.75;">
                ${params.body}
              </p>
              ${cta}
            </td>
          </tr>
          <tr>
            <td align="center" style="padding-top:28px;">
              <p style="margin:0;font-size:12px;color:#94a3b8;line-height:1.6;">
                David's Beacon &middot; Safety for every family
              </p>
              <p style="margin:4px 0 0;font-size:12px;color:#94a3b8;">
                If you didn't expect this email, you can safely ignore it.
              </p>
              <p style="margin:8px 0 0;font-size:11px;color:#cbd5e1;">
                <a href="${REACT_BASE_URL}/privacy" style="color:#94a3b8;text-decoration:none;">Privacy Policy</a>
                &nbsp;&middot;&nbsp;
                <a href="${REACT_BASE_URL}/terms" style="color:#94a3b8;text-decoration:none;">Terms of Use</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
}

export const DEFAULT_TEMPLATES: Record<string, EmailTemplateData> = {
  co_carer_invite: {
    subject: "{{inviter_name}} invited you to help manage {{profile_name}} on David's Beacon",
    title: "You've been invited to help manage {{profile_name}}",
    body: "{{inviter_name}} has invited you to help manage <strong>{{profile_name}}</strong> as <strong>{{role}}</strong> on David's Beacon.",
    ctaText: 'Accept Invitation',
  },
  org_staff_invite: {
    subject: "You've been invited to join {{org_name}} on David's Beacon",
    title: "You've been invited to join {{org_name}}",
    body: "{{sender_name}} has invited you to join <strong>{{org_name}}</strong> as a team member on David's Beacon. Accept the invitation to get started.",
    ctaText: 'Accept Invitation',
  },
  org_client_invite: {
    subject: "{{org_name}} wants to connect with your family on David's Beacon",
    title: '{{org_name}} sent your family an invitation',
    body: "{{sender_name}} from <strong>{{org_name}}</strong> would like to connect with your family's safety profile on David's Beacon. Review the invitation before accepting.",
    ctaText: 'View Invitation',
  },
  org_application_received: {
    subject: 'New org application: {{org_name}}',
    title: 'New org application: {{org_name}}',
    body: "A new organization has applied to join David's Beacon. Review the details below and approve or reject in Command Center.",
    ctaText: 'Open Command Center',
  },
  org_application_rejected: {
    subject: "Update on your David's Beacon organization application",
    title: 'Update on your application',
    body: "Thank you for applying to register <strong>{{org_name}}</strong> on David's Beacon.<br><br>After review, we're unable to approve your application at this time.{{rejection_reason}}<br><br>If you have questions or would like to reapply with updated information, please don't hesitate to reach out.",
    ctaText: null,
  },
  org_application_approved: {
    subject: "{{org_name}} has been approved on David's Beacon!",
    title: 'Your organization has been approved',
    body: "Great news, {{contact_name}}! <strong>{{org_name}}</strong> has been approved on David's Beacon. You can now set up your organization profile, invite staff, and connect with families.<br><br>Welcome aboard!",
    ctaText: 'Go to My Organization',
  },
}

export const TEMPLATE_META: TemplateMeta[] = [
  {
    id: 'co_carer_invite',
    label: 'Co-carer Invite',
    description: 'Sent when a family member invites someone to help manage a safety profile.',
    ctaUrl: `${REACT_BASE_URL}/invite/preview`,
    tags: [
      { key: 'inviter_name', label: "Inviter's Name", sample: 'Maria Santos' },
      { key: 'profile_name', label: 'Profile Name', sample: 'David Santos' },
      { key: 'role', label: 'Role Label', sample: 'Therapist' },
    ],
  },
  {
    id: 'org_staff_invite',
    label: 'Staff Invite',
    description: 'Sent when an org admin invites a staff member to join their organization.',
    ctaUrl: `${REACT_BASE_URL}/org-invite/preview`,
    tags: [
      { key: 'sender_name', label: "Sender's Name", sample: 'Admin Maria' },
      { key: 'org_name', label: 'Organization Name', sample: 'Sunshine Therapy Center' },
    ],
  },
  {
    id: 'org_client_invite',
    label: 'Client Invite',
    description: 'Sent when an org invites a family to connect their profiles.',
    ctaUrl: `${REACT_BASE_URL}/org-invite/preview`,
    tags: [
      { key: 'sender_name', label: "Sender's Name", sample: 'Admin Maria' },
      { key: 'org_name', label: 'Organization Name', sample: 'Sunshine Therapy Center' },
    ],
  },
  {
    id: 'org_application_received',
    label: 'Application Received',
    description: 'Sent when a new organization applies to join the platform.',
    ctaUrl: `${REACT_BASE_URL}/command-center`,
    tags: [
      { key: 'org_name', label: 'Organization Name', sample: 'Sunshine Therapy Center' },
      { key: 'contact_name', label: "Contact's Name", sample: 'Maria Santos' },
    ],
  },
  {
    id: 'org_application_rejected',
    label: 'Application Rejected',
    description: 'Sent to an applicant when their organization application is rejected.',
    tags: [
      { key: 'contact_name', label: "Contact's Name", sample: 'Maria Santos' },
      { key: 'org_name', label: 'Organization Name', sample: 'Sunshine Therapy Center' },
      { key: 'rejection_reason', label: 'Rejection Reason', sample: '<br><br><strong>Feedback:</strong> Please provide your official registration documents.' },
    ],
  },
  {
    id: 'org_application_approved',
    label: 'Application Approved',
    description: 'Sent to the applicant when you approve their organization.',
    ctaUrl: `${REACT_BASE_URL}/org`,
    tags: [
      { key: 'contact_name', label: "Contact's Name", sample: 'Maria Santos' },
      { key: 'org_name', label: 'Organization Name', sample: 'Sunshine Therapy Center' },
    ],
  },
]
